import React, { useCallback } from 'react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import AkFlag, { FlagGroup } from '@atlaskit/flag';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { token } from '@atlaskit/tokens';

import { dismissDiagramPasteFlag } from '../../editor-commands/dismiss-diagram-paste-flag';
import type { MalleableUiPlugin } from '../../malleableUiPluginType';
import { MauiDiagramPasteFlagType } from '../../pm-plugins/maui-diagram-paste-plugin';

type Props = {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
};

export const MauiDiagramPasteFlag = ({ api }: Props): React.JSX.Element | null => {
	const activeFlag = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.diagramPaste?.activeFlag ?? null,
	);

	const onDismissed = useCallback(() => {
		api?.core.actions.execute(dismissDiagramPasteFlag());
	}, [api]);

	if (!activeFlag) {
		return null;
	}

	const title = activeFlag.title ?? '';
	const description = activeFlag.description ?? '';

	const icon = (() => {
		switch (activeFlag.type) {
			case MauiDiagramPasteFlagType.SUCCESS:
				return <CheckCircleIcon label="" color={token('color.icon.success')} />;
			case MauiDiagramPasteFlagType.INFO:
				return <StatusInformationIcon label="" color={token('color.icon.information')} />;
			case MauiDiagramPasteFlagType.ERROR:
			default:
				return <StatusErrorIcon label="" color={token('color.icon.danger')} />;
		}
	})();

	return (
		<FlagGroup>
			<AkFlag
				onDismissed={onDismissed}
				title={title}
				description={description}
				id={activeFlag.id}
				testId={activeFlag.id}
				icon={icon}
			/>
		</FlagGroup>
	);
};
